import { Customer } from "@/shared/interfaces/customer/Customer.interface";

const MIN_BILLBOARD_STREET_LENGTH = 6;

function toTrimmedString(value: unknown): string {
    return typeof value === "string" ? value.trim() : "";
}

function hasAtLeastOneDigit(value: string): boolean {
    return /\d/.test(value);
}

function isRepeatedSingleCharacter(value: string): boolean {
    return value.length > 2 && new Set(value.toLowerCase()).size === 1;
}

function isValidLocality(value: string): boolean {
    if (!value) {
        return false;
    }

    const hasLetter = /[a-zA-Z]/.test(value);
    if (!hasLetter) {
        return false;
    }

    if (/^x+$/i.test(value) || isRepeatedSingleCharacter(value)) {
        return false;
    }

    return true;
}

function hasValidStreetNumber(street: string, streetNumber: string): boolean {
    if (streetNumber) {
        return hasAtLeastOneDigit(streetNumber) && !/^x+$/i.test(streetNumber);
    }

    const streetParts = street
        .split(",")
        .map(part => part.trim())
        .filter(Boolean);

    const streetLabel = streetParts[0] || street;
    const mergedStreetNumber = streetParts.length > 1 ? streetParts[1] : "";

    if (mergedStreetNumber) {
        return hasAtLeastOneDigit(mergedStreetNumber) && !/^x+$/i.test(mergedStreetNumber);
    }

    return hasAtLeastOneDigit(streetLabel);
}

// Municipalities taxing billboards heavily (Saint-Josse, EUR 50/billboard/month): billboard can't be offered.
const BILLBOARD_TAXED_POSTAL_CODES = ["1210"];

export function isBillboardTaxedPostalCode(postalCode: unknown): boolean {
    return postalCode != null && BILLBOARD_TAXED_POSTAL_CODES.includes(String(postalCode).trim());
}

export function isBillboardSelected(
    getTransactionValue: (path: string) => unknown,
    getOrderValue?: (path: string) => unknown
): boolean {
    const selectedPackage = getTransactionValue("publication.visualizationOption");
    const selectedOrderPackage = getOrderValue ? getOrderValue("order.visualizationOption") : null;
    const selectedPackOptions = (getTransactionValue("optimizedEncoding.selectedPackOptions") || {}) as Record<string, { billboard?: unknown }>;
    const currentBillboardPricing = getTransactionValue("transaction.pricing.billboard");

    if (currentBillboardPricing) {
        return true;
    }

    const preferredPackKey = typeof selectedPackage === "string"
        ? selectedPackage.toLowerCase()
        : "";
    const fallbackPackKey = typeof selectedOrderPackage === "string"
        ? selectedOrderPackage.toLowerCase()
        : "";
    const selectedPackKey = preferredPackKey || fallbackPackKey;

    if (selectedPackKey) {
        const resolvedPackKey = Object.keys(selectedPackOptions).find(
            key => key.toLowerCase() === selectedPackKey
        );

        if (resolvedPackKey) {
            return !!selectedPackOptions[resolvedPackKey].billboard;
        }
    }

    return false;
}

export function hasValidBillboardBillingAddress(
    customer: Customer | null | undefined,
    minStreetLength = MIN_BILLBOARD_STREET_LENGTH
): boolean {
    const providedAddress = customer?.location?.address?.provided;
    if (!providedAddress) {
        return false;
    }

    const street = toTrimmedString(providedAddress.street);
    const streetNumber = toTrimmedString(providedAddress.streetNumber);
    const postalCode = toTrimmedString(providedAddress.postalCode);
    const locality = toTrimmedString(providedAddress.locality);

    const streetLabel = street
        .split(",")
        .map(part => part.trim())
        .filter(Boolean)[0] || street;

    const hasStreet = streetLabel.length >= minStreetLength;
    const hasStreetNumber = hasValidStreetNumber(street, streetNumber);
    const hasPostalCode = postalCode.length > 0;
    const hasLocality = isValidLocality(locality);

    return hasStreet && hasStreetNumber && hasPostalCode && hasLocality;
}
